import React, { useState, useEffect, useCallback } from 'react'
import Select from 'react-select'
import { SlidersHorizontal, X, Calendar, ChevronDown, ChevronUp, FileText, Scale } from 'lucide-react'
import useStore from '../store/useStore'
import { cachedApiService } from '../services/api'
import { debounce } from '../utils/helpers'

const FilterPanel = ({ isCollapsible = false, defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const {
    filters,
    updateFilter,
    clearFilters,
    hasFilters,
    getActiveFiltersCount,
    getFilterSummary,
    filterOptions,
    setFilterOptions
  } = useStore()

  const [loadingOptions, setLoadingOptions] = useState({
    nif_importateur: false,
    pays_origine: false,
    incoterm: false,
    code_regime_apc: false,
    annee_declaration: false,
  })

  // Fonction pour construire l'état complet des filtres
  const buildCurrentFilters = useCallback(() => {
    return {
      produits: filters.produits || [],
      fournisseurs: filters.fournisseurs || [],
      importateurs: filters.importateurs || [],
      nif_importateur: filters.nif_importateur || [],
      pays_origine: filters.pays_origine || [],
      incoterm: filters.incoterm || [],
      code_regime_apc: filters.code_regime_apc || [],
      date_debut: filters.date_debut || null,
      date_fin: filters.date_fin || null,
      annee_declaration: filters.annee_declaration || []
    }
  }, [filters])

  // Debounced search functions
  const debouncedSearch = useCallback(
    debounce(async (filterType, query) => {
      setLoadingOptions(prev => ({ ...prev, [filterType]: true }))
      try {
        const currentFilters = buildCurrentFilters()
        const result = await cachedApiService.getFilterOptions(filterType, query, currentFilters)
        if (result && result.options && Array.isArray(result.options)) {
          setFilterOptions(filterType, result.options)
        } else {
          setFilterOptions(filterType, [])
        }
      } catch (error) {
        console.error(`Error loading ${filterType}:`, error)
      } finally {
        setLoadingOptions(prev => ({ ...prev, [filterType]: false }))
      }
    }, 300),
    []
  )

  // Load initial options
  useEffect(() => {
    const loadInitialOptions = async () => {
      const filterTypes = ['nif_importateur', 'pays_origine', 'incoterm', 'code_regime_apc', 'annee_declaration']
      
      for (const filterType of filterTypes) {
        debouncedSearch(filterType, '')
      }
    }

    loadInitialOptions()
  }, [debouncedSearch])

  // Reload options when main filters change (cascading filters)
  useEffect(() => {
    const filterTypes = ['nif_importateur', 'pays_origine', 'incoterm', 'code_regime_apc', 'annee_declaration']
    
    // Check if there are any active main filters (produits, importateurs, fournisseurs)
    const hasMainFilters = filters.produits?.length > 0 || 
                          filters.importateurs?.length > 0 || 
                          filters.fournisseurs?.length > 0
    
    if (hasMainFilters) {
      filterTypes.forEach(filterType => {
        debouncedSearch(filterType, '')
      })
    }
  }, [filters.produits, filters.importateurs, filters.fournisseurs, debouncedSearch])

  const handleSelectChange = (filterName, selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(option => option.value) : []
    updateFilter(filterName, values)
  }

  const handleDateChange = (filterName, value) => {
    updateFilter(filterName, value)
  }

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      borderRadius: '0.5rem',
      borderColor: state.isFocused ? '#3b82f6' : 'var(--border-color, #e5e7eb)',
      backgroundColor: 'var(--select-bg, white)',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      '&:hover': { borderColor: '#93c5fd' }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.75rem',
      overflow: 'hidden',
      boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '13px',
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '13px',
      cursor: 'pointer',
    }),
  }

  const formatOptionLabel = (option) => (
    <div className="flex justify-between items-center">
      <span className="truncate">{option.label}</span>
      {option.count && (
        <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
          {option.count.toLocaleString()}
        </span>
      )}
    </div>
  )

  return (
    <div className={isCollapsible ? "card-sm" : "card"}>
      <div className={`flex items-center justify-between ${isCollapsible ? 'mb-3' : 'mb-5'}`}>
        <div className="flex items-center space-x-2">
          <SlidersHorizontal className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <h2 className={`font-semibold text-gray-900 dark:text-gray-100 ${isCollapsible ? 'text-sm' : 'text-base'}`}>
            {isCollapsible ? 'Filtres avancés' : 'Filtres'}
          </h2>
          {hasFilters && (
            <span className="bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {getActiveFiltersCount()}
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center space-x-1 text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              <span>Réinitialiser</span>
            </button>
          )}

          {isCollapsible && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center space-x-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5" />
                  <span>Masquer</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5" />
                  <span>Afficher</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {hasFilters && isExpanded && (
        <div className="mb-3 p-2.5 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/40 rounded-lg">
          <p className="text-xs text-primary-800 dark:text-primary-300">
            <span className="font-medium">Filtres actifs:</span> {getFilterSummary()}
          </p>
        </div>
      )}

      {isExpanded && (
        <div>
          <div className={`grid field-gap ${isCollapsible ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'space-y-4'}`}>


        {/* Incoterms */}
        <div>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
            <FileText className="w-3.5 h-3.5" />
            Incoterms
          </label>
          <Select
            isMulti
            value={(Array.isArray(filterOptions.incoterm) ? filterOptions.incoterm : []).filter(option => 
              filters.incoterm?.includes(option.value)
            )}
            options={Array.isArray(filterOptions.incoterm) ? filterOptions.incoterm : []}
            onChange={(selected) => handleSelectChange('incoterm', selected)}
            onInputChange={(query) => debouncedSearch('incoterm', query)}
            placeholder="Sélectionner des incoterms..."
            noOptionsMessage={() => "Aucun incoterm trouvé"}
            loadingMessage={() => "Recherche..."}
            isLoading={loadingOptions.incoterm}
            styles={customSelectStyles}
            formatOptionLabel={formatOptionLabel}
            className="react-select-container"
            classNamePrefix="react-select"
            isClearable
            isSearchable
            closeMenuOnSelect={false}
            blurInputOnSelect={false}
          />
        </div>


        {/* Code Régime APC */}
        <div>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
            <Scale className="w-3.5 h-3.5" />
            Code Régime APC
          </label>
          <Select
            isMulti
            value={(Array.isArray(filterOptions.code_regime_apc) ? filterOptions.code_regime_apc : []).filter(option => 
              filters.code_regime_apc?.includes(option.value)
            )}
            options={Array.isArray(filterOptions.code_regime_apc) ? filterOptions.code_regime_apc : []}
            onChange={(selected) => handleSelectChange('code_regime_apc', selected)}
            onInputChange={(query) => debouncedSearch('code_regime_apc', query)}
            placeholder="Sélectionner des codes régime..."
            noOptionsMessage={() => "Aucun code régime trouvé"}
            loadingMessage={() => "Recherche..."}
            isLoading={loadingOptions.code_regime_apc}
            styles={customSelectStyles}
            formatOptionLabel={formatOptionLabel}
            className="react-select-container"
            classNamePrefix="react-select"
            isClearable
            isSearchable
            closeMenuOnSelect={false}
            blurInputOnSelect={false}
          />
        </div>

        {/* Année de déclaration */}
        <div>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
            <Calendar className="w-3.5 h-3.5" />
            Année de déclaration
          </label>
          <Select
            isMulti
            value={(Array.isArray(filterOptions.annee_declaration) ? filterOptions.annee_declaration : []).filter(option => 
              filters.annee_declaration?.includes(option.value)
            )}
            options={Array.isArray(filterOptions.annee_declaration) ? filterOptions.annee_declaration : []}
            onChange={(selected) => handleSelectChange('annee_declaration', selected)}
            onInputChange={(query) => debouncedSearch('annee_declaration', query)}
            placeholder="Sélectionner des années..."
            noOptionsMessage={() => "Aucune année trouvée"}
            loadingMessage={() => "Recherche..."}
            isLoading={loadingOptions.annee_declaration}
            styles={customSelectStyles}
            formatOptionLabel={formatOptionLabel}
            className="react-select-container"
            classNamePrefix="react-select"
            isClearable
            isSearchable
            closeMenuOnSelect={false}
            blurInputOnSelect={false}
          />
        </div>

        {/* Période */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              <Calendar className="w-3.5 h-3.5" />
              Date de début
            </label>
            <input
              type="date"
              value={filters.date_debut || ''}
              onChange={(e) => handleDateChange('date_debut', e.target.value || null)}
              className="input-field"
            />
          </div>
          
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              <Calendar className="w-3.5 h-3.5" />
              Date de fin
            </label>
            <input
              type="date"
              value={filters.date_fin || ''}
              onChange={(e) => handleDateChange('date_fin', e.target.value || null)}
              className="input-field"
            />
          </div>
        </div>
          </div>
          
          {!hasFilters && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Sélectionnez au moins un filtre pour afficher les données
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default FilterPanel
